/**
 * HTTP client.
 *
 * Owns the concerns the rest of the app should never repeat: attaching the
 * access token, unwrapping the `{ success, data }` envelope, and turning an
 * error body into an `ApiError`. A partner session is a single 12 h token with
 * no refresh, so a 401 on an authenticated call simply ends the session.
 */

import type { ApiResponse, PaginatedResponse, ProblemDetail } from '@/types/api'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/** A failed request, carrying the server's problem document. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly problem: ProblemDetail | null
  readonly fieldErrors: Record<string, string>

  constructor(message: string, status: number, code: string, problem: ProblemDetail | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.problem = problem
    this.fieldErrors = Object.fromEntries(
      (problem?.errors ?? [])
        .filter((e) => e.field)
        .map((e) => [e.field as string, e.message]),
    )
  }

  /** True when retrying verbatim could plausibly succeed. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429
  }
}

type TokenGetter = () => string | null
type UnauthorizedHandler = (error: ApiError) => void

let getAccessToken: TokenGetter = () => null
let onUnauthorized: UnauthorizedHandler = () => {}

export function configureClient(options: { getAccessToken: TokenGetter; onUnauthorized: UnauthorizedHandler }): void {
  getAccessToken = options.getAccessToken
  onUnauthorized = options.onUnauthorized
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  query?: Record<string, unknown>
  /** Set false for the login call itself. */
  auth?: boolean
  raw?: boolean
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  const url = `${BASE_URL}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function toApiError(response: Response): Promise<ApiError> {
  let problem: ProblemDetail | null = null
  try {
    problem = (await response.json()) as ProblemDetail
  } catch {
    // A non-JSON error body (a proxy 502, say) still deserves a clean message.
  }
  const message = problem?.detail || problem?.title || `Request failed with status ${response.status}`
  return new ApiError(message, response.status, problem?.code ?? 'unknown_error', problem)
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, auth = true, headers, raw, ...rest } = options
  const requestHeaders = new Headers(headers)
  if (body !== undefined) requestHeaders.set('Content-Type', 'application/json')
  if (auth) {
    const token = getAccessToken()
    if (token) requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      ...rest,
      headers: requestHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.', 0, 'network_error', null)
  }

  if (!response.ok) {
    const error = await toApiError(response)
    // The token expired, or the account was suspended while signed in.
    if (auth && (error.status === 401 || error.code === 'partner_suspended')) onUnauthorized(error)
    throw error
  }
  if (response.status === 204) return undefined as T

  const payload = await response.json()
  if (raw) return payload as T
  return (payload as ApiResponse<T>).data ?? (payload as T)
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>) => request<T>(path, { method: 'GET', query }),

  /** Keeps `pageInfo` alongside the rows, for paginated endpoints. */
  getPage: <T>(path: string, query?: Record<string, unknown>) =>
    request<PaginatedResponse<T>>(path, { method: 'GET', query, raw: true }),

  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),

  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /** Unauthenticated POST, used by login. */
  postPublic: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body, auth: false }),
}
